import { useState, useSyncExternalStore } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useQuery } from '@tanstack/react-query'
import { Mail, Trash, X } from 'lucide-react'

import {
  editableSeatCostCenterIds,
  inviteSubtitle,
  isResendableInvite,
  isSeatEditable,
  shouldRenderRoleDropdown,
  wasResent,
} from './memberTableUtils'
import { RoleDropdown, roleTitle } from './RoleDropdown'
import { SeatChangeBar } from './SeatChangeBar'
import { SeatTypeDropdown, SelectableSeatType } from './SeatTypeDropdown'
import { useSeatChangeDraft } from './useSeatChangeDraft'

import styles from './MemberTable.module.scss'

import { listCostCenters } from '@/api/costCenters'
import anon from '@/assets/images/anon.png'
import AvatarImage from '@/components/AvatarImage'
import ConfirmDialog from '@/components/ConfirmDialog'
import { Spinner } from '@/components/Spinner'
import { useToast } from '@/components/Toast/ToastContext'
import Tooltip from '@/components/Tooltip/Tooltip'
import { useSeatSelectionEnabled } from '@/hooks/useSeatManagementEnabled'
import { useSortedMembers } from '@/hooks/useSortedMembers'
import { useTeamMemberActions } from '@/hooks/useTeamMemberActions'
import { getImpersonation, subscribeImpersonation } from '@/lib/impersonation'
import { COST_CENTERS_QUERY_KEY } from '@/pages/app/Settings/CostCenters'
import { AppDispatch } from '@/store'
import { selectResendingInviteIds, updateMemberRoleThunk } from '@/store/features/member/memberSlice'
import { selectCurrentEnterprise, selectEmail, selectIsAdmin } from '@/store/features/user/userSlice'
import { Invite, Member, Role } from '@/types'
import { calculateTrialDays } from '@/utils/dates'
import { isMemberOnTrial } from '@/utils/subscription'

// Rows whose seat can't be changed show the same select, disabled.
const SeatTypeCell = ({
  role,
  seatType,
  name,
}: {
  role: string
  seatType: string | null | undefined
  name: string
}) => (
  <div className={styles.seatType}>
    <SeatTypeDropdown role={role} seatType={seatType} ariaLabel={`Seat type for ${name}`} disabled />
  </div>
)

const memberName = (member: Member) => `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim() || member.email

interface SeatEdit {
  seatType: string | null | undefined
  isStaged: boolean
  disabled: boolean
  onSelect: (seatType: SelectableSeatType) => void
}

const EditableSeatCell = ({ member, edit }: { member: Member; edit: SeatEdit }) => (
  <div className={styles.seatType}>
    <SeatTypeDropdown
      role={member.role}
      seatType={edit.seatType}
      onSelect={edit.onSelect}
      disabled={edit.disabled}
      ariaLabel={`Seat type for ${memberName(member)}${edit.isStaged ? ' (unsaved)' : ''}`}
    />
  </div>
)

interface UserRowProps {
  user: Member
  isSelf: boolean
  adminView: boolean
  openRemoveModal: () => void
  onRemoveIntent: () => void
  isScimEnabled: boolean
  actionClassName: string
  showSeatType: boolean
  seatEdit?: SeatEdit
}

const UserRow = (props: UserRowProps) => {
  const {
    user: member,
    isSelf,
    adminView,
    openRemoveModal,
    onRemoveIntent,
    isScimEnabled,
    actionClassName,
    showSeatType,
    seatEdit,
  } = props
  const dispatch = useDispatch<AppDispatch>()
  const { updateToast } = useToast()

  const isAdmin = useSelector(selectIsAdmin)
  const currentRole = member.role?.toLowerCase() as Role | undefined

  const [pendingRole, setPendingRole] = useState<Role | null>(null)
  const [isChangingRole, setIsChangingRole] = useState(false)

  const commitRoleChange = async (role: Role) => {
    try {
      setIsChangingRole(true)
      await dispatch(updateMemberRoleThunk({ userId: member.userId, role })).unwrap()
      updateToast('Updated role', 'success')
    } catch (error) {
      updateToast(error as string, 'error')
    } finally {
      setIsChangingRole(false)
      setPendingRole(null)
    }
  }

  const changeRole = async (role: Role) => {
    if (role === currentRole) return
    if (isSelf && role === 'member') {
      updateToast("You can't change your own role to Member — ask another admin to update your role.", 'error')
      return
    }
    // Changes to or from IT Admin flip billing/product-access state for the
    // user — confirm before committing.
    if (role === 'it_admin' || currentRole === 'it_admin') {
      setPendingRole(role)
      return
    }
    await commitRoleChange(role)
  }

  const isPendingToItAdmin = pendingRole === 'it_admin'

  return (
    <div className={`${styles.userRow} ${seatEdit?.isStaged ? styles.userRowStaged : ''}`}>
      <div className={styles.profile}>
        <AvatarImage avatarUrl={member.avatarUrl} />
        <div className={styles.userInfo}>
          <div className={styles.name}>
            {member.firstName} {member.lastName ?? ''}
            {isSelf && <span className={styles.selfTag}>You</span>}
          </div>
          <div className={styles.email}>{member.email}</div>
        </div>
      </div>

      <div className={styles.trialStatusText}>
        {isMemberOnTrial(member.trialEndsAt)
          ? `Trialing for ${calculateTrialDays(member.trialEndsAt!)} days`
          : 'Active'}
      </div>
      <div className={styles.role}>
        {shouldRenderRoleDropdown({ adminView, isSelf, memberRole: member.role }) ? (
          <RoleDropdown
            key={`role-${member.userId}`}
            currentRole={member.role.toLowerCase() as Role}
            changeRole={changeRole}
            selfEdit={isSelf}
          />
        ) : member.role ? (
          roleTitle[member.role.toLowerCase() as Role]
        ) : (
          'Unknown'
        )}
      </div>
      {showSeatType &&
        (seatEdit ? (
          <EditableSeatCell member={member} edit={seatEdit} />
        ) : (
          <SeatTypeCell role={member.role} seatType={member.seatType} name={memberName(member)} />
        ))}
      {isAdmin && !isScimEnabled && (
        <div className={actionClassName}>
          {!isSelf && (
            <Tooltip text="Remove member" placement="top" delay={200} hideArrow>
              <button
                type="button"
                className={styles.removeButton}
                onClick={() => openRemoveModal()}
                onMouseEnter={onRemoveIntent}
                onFocus={onRemoveIntent}
                aria-label="Remove member"
              >
                <Trash size={16} aria-hidden />
              </button>
            </Tooltip>
          )}
        </div>
      )}
      <ConfirmDialog
        isOpen={pendingRole !== null}
        title={isPendingToItAdmin ? 'Change role to IT Admin?' : 'Change role from IT Admin?'}
        message={
          isPendingToItAdmin
            ? 'IT Admins can manage the team, billing, and SSO but cannot use Wispr Flow dictation. This member will lose product access.'
            : 'This member will gain product access and count toward your paid seats.'
        }
        confirmLabel="Change role"
        cancelLabel="Cancel"
        confirmType="dark"
        isConfirming={isChangingRole}
        onConfirm={() => {
          if (pendingRole) void commitRoleChange(pendingRole)
        }}
        onCancel={() => setPendingRole(null)}
      />
    </div>
  )
}

interface ResendInviteButtonProps {
  isResending: boolean
  onResend: () => void
}

const ResendInviteButton = ({ isResending, onResend }: ResendInviteButtonProps) => (
  <Tooltip text="Resend invite" placement="top" delay={200} hideArrow>
    <button
      type="button"
      className={styles.removeButton}
      onClick={onResend}
      disabled={isResending}
      aria-busy={isResending}
      aria-label="Resend invite"
    >
      {isResending ? <Spinner size={16} color="currentColor" /> : <Mail size={16} aria-hidden />}
    </button>
  </Tooltip>
)

interface InviteRowProps {
  invite: Invite
  openRevokeModal: () => void
  isScimEnabled: boolean
  actionClassName: string
  resend?: ResendInviteButtonProps
  showSeatType: boolean
}

const InviteRow = (props: InviteRowProps) => {
  const { invite, openRevokeModal, isScimEnabled, actionClassName, resend, showSeatType } = props
  const isAdmin = useSelector(selectIsAdmin)

  const formatStatus = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
  }

  if (!invite || !invite.email) {
    return null
  }

  return (
    <div className={styles.userRow}>
      <div className={styles.profile}>
        <img src={anon} alt="Anon" />
        <div className={styles.userInfo}>
          <div className={styles.name}>{invite.email}</div>
          <div className={styles.email}>{inviteSubtitle(invite)}</div>
        </div>
      </div>
      <div className={styles.role}>{formatStatus(invite.status)}</div>
      {showSeatType && <SeatTypeCell role={invite.role} seatType={invite.intended_seat_type} name={invite.email} />}
      {isAdmin && !isScimEnabled && (
        <div className={actionClassName}>
          {resend && <ResendInviteButton {...resend} />}
          <Tooltip text="Revoke invite" placement="top" delay={200} hideArrow>
            <button
              type="button"
              className={styles.removeButton}
              onClick={() => openRevokeModal()}
              aria-label="Revoke invite"
            >
              <X size={16} aria-hidden />
            </button>
          </Tooltip>
        </div>
      )}
    </div>
  )
}

interface MemberTableProps {
  setMemberToRemove: (member: Member) => void
  prefetchRemoval: (member: Member) => void
  setInviteToRevoke: (invite: Invite) => void
  isScimEnabled: boolean
  // Stays mounted while another Team tab is open, rendering no rows, so staged seat changes survive.
  hidden?: boolean
}

export default function MemberTable(props: MemberTableProps) {
  const { setMemberToRemove, prefetchRemoval, setInviteToRevoke, isScimEnabled, hidden = false } = props
  const { members, invites } = useSortedMembers()
  const isAdmin = useSelector(selectIsAdmin)
  const enterprise = useSelector(selectCurrentEnterprise)
  const currentEmail = useSelector(selectEmail)
  const resendingInviteIds = useSelector(selectResendingInviteIds)
  const seatSelectionEnabled = useSeatSelectionEnabled()
  const canResend = isAdmin && !isScimEnabled
  // Decided once for both row types so member and invite cells always line up.
  const showSeatType = isAdmin && seatSelectionEnabled
  const enterpriseId = enterprise?.id ?? ''
  // Support viewing as an admin never changes seats, whatever a cached cost-center list says.
  const impersonating = useSyncExternalStore(subscribeImpersonation, getImpersonation) !== null
  const canEditSeats = showSeatType && !impersonating && Boolean(enterpriseId)
  const { data: costCenters } = useQuery({
    queryKey: COST_CENTERS_QUERY_KEY(enterpriseId),
    queryFn: async () => (await listCostCenters(enterpriseId)).data.cost_centers,
    enabled: canEditSeats,
  })
  const editableCostCenterIds = editableSeatCostCenterIds(canEditSeats ? costCenters : undefined)
  const draft = useSeatChangeDraft(members, enterpriseId, editableCostCenterIds)
  const { resendInvites } = useTeamMemberActions()
  const { updateToast } = useToast()
  const actionClassName = canResend ? `${styles.action} ${styles.actionWide}` : styles.action

  const resendInvite = async (invite: Invite) => {
    const result = (await resendInvites([invite.id]))?.[0]
    if (!result) return
    if (wasResent(result)) {
      updateToast(`Invite resent to ${invite.email}`, 'success')
      return
    }
    switch (result.outcome) {
      case 'ineligible':
        updateToast("This invite can't be resent. It may have been accepted or revoked.", 'error')
        break
      case 'failed':
        updateToast("Couldn't resend the invite. If this keeps happening, revoke it and add them again.", 'error')
        break
    }
  }

  const seatEditFor = (member: Member): SeatEdit | undefined => {
    if (!isSeatEditable(member, editableCostCenterIds)) return undefined

    const stagedSeat = draft.staged[member.userId]
    const isStaged = stagedSeat !== undefined && stagedSeat !== member.seatType

    return {
      seatType: isStaged ? stagedSeat : member.seatType,
      isStaged,
      disabled: draft.isLocked,
      onSelect: (seatType) => draft.stage(member, seatType),
    }
  }

  const { leaveBlocker } = draft
  // Once a save was sent, its changes may still apply after the admin leaves.
  const saveWasSent = draft.isSaving || draft.isUnresolved

  return (
    <>
      <ConfirmDialog
        isOpen={leaveBlocker.state === 'blocked'}
        title={saveWasSent ? 'Leave before the save is confirmed?' : 'Leave without saving?'}
        message={
          saveWasSent
            ? "We haven't confirmed your seat changes yet. They may still be applied."
            : `${draft.pendingCount} seat change${draft.pendingCount === 1 ? '' : 's'} won't be saved.`
        }
        confirmLabel="Leave"
        cancelLabel="Stay"
        onConfirm={() => leaveBlocker.proceed?.()}
        onCancel={() => leaveBlocker.reset?.()}
      />
      {!hidden && (
        <>
          <SeatChangeBar
            pendingCount={draft.pendingCount}
            isSaving={draft.isSaving}
            isUnresolved={draft.isUnresolved}
            onSave={draft.save}
            onDiscard={draft.discard}
          />
          <div className={styles.usersTable}>
            {members.map((member: Member) => (
              <UserRow
                key={member.userId}
                actionClassName={actionClassName}
                user={member}
                isSelf={member.email === currentEmail}
                adminView={isAdmin}
                openRemoveModal={() => setMemberToRemove(member)}
                onRemoveIntent={() => prefetchRemoval(member)}
                isScimEnabled={isScimEnabled}
                showSeatType={showSeatType}
                seatEdit={seatEditFor(member)}
              />
            ))}
            {invites.map((invite: Invite) => (
              <InviteRow
                key={invite.id}
                invite={invite}
                openRevokeModal={() => setInviteToRevoke(invite)}
                isScimEnabled={isScimEnabled}
                actionClassName={actionClassName}
                showSeatType={showSeatType}
                resend={
                  canResend && isResendableInvite(invite)
                    ? {
                        isResending: !!resendingInviteIds[invite.id],
                        onResend: () => void resendInvite(invite),
                      }
                    : undefined
                }
              />
            ))}
          </div>
        </>
      )}
    </>
  )
}
